import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import { useOptions } from '../../stateManagers/hooks/useOptions';
import AttendanceBarcodeModal from './parts/AttendanceBarcodeModal';
import ProfilePictureHover from '../../components/ProfilePictureHover';

interface AttendanceRecord {
  id: number | null;
  user_id: number;
  user_role: string;
  attendance_date: string;
  scan_type: 'checkin' | 'checkout' | null;
  scan_time: string | null;
  status: 'present' | 'absent' | 'late' | 'unmarked';
  distance_m: number | null;
  stage2_used: 0 | 1;
  created_at: string;
  user_name: string;
  user_email: string;
  profile_picture?: string | null;
  is_unmarked_placeholder?: boolean;
}

interface PaginationData {
  current_page: number;
  total_pages: number;
  total_items: number;
  per_page: number;
  has_next: boolean;
  has_previous: boolean;
}

interface AttendanceLocation {
  lat: number;
  lng: number;
  radius: number;
  enabled: boolean;
  label?: string;
}

interface WindowOption {
  enabled: boolean;
  start: string;
  end: string;
}

interface GpsOnlyOption {
  enabled: boolean;
}

interface ScanBucket {
  present: number;
  absent: number;
  late: number;
  total: number;
}

interface StatsMonthEntry {
  month: string;
  checkin: ScanBucket;
  checkout: ScanBucket;
}

interface StatsUserEntry {
  user_id: number;
  user_name: string;
  role: string;
  profile_picture?: string | null;
  checkin?: ScanBucket;
  checkout?: ScanBucket;
  months?: StatsMonthEntry[];
  totals?: { checkin: ScanBucket; checkout: ScanBucket };
}

interface StatsSummary {
  checkin: ScanBucket;
  checkout: ScanBucket;
}

const Ico = ({ d, size = 16, color = 'currentColor', style = {}, strokeWidth = 1.8 }: {
  d: string | string[]; size?: number; color?: string; style?: React.CSSProperties; strokeWidth?: number;
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'block', ...style }}>
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const IcoShield   = (p: any) => <Ico {...p} d={['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z']} />;
const IcoQr       = (p: any) => <Ico {...p} d={['M3 3h6v6H3zM15 3h6v6h-6zM3 15h6v6H3zM15 15h3v3h-3zM21 15v3M15 21h6M21 18v3']} />;
const IcoMapPin   = (p: any) => <Ico {...p} d={['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z','M12 10a2 2 0 100-4 2 2 0 000 4z']} />;
const IcoUsers    = (p: any) => <Ico {...p} d={['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2','M9 11a4 4 0 100-8 4 4 0 000 8z','M23 21v-2a4 4 0 00-3-3.87','M16 3.13a4 4 0 010 7.75']} />;
const IcoCheck    = (p: any) => <Ico {...p} d="M20 6L9 17l-5-5" />;
const IcoAlert    = (p: any) => <Ico {...p} d={['M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z','M12 9v4','M12 17h.01']} />;
const IcoChevL    = (p: any) => <Ico {...p} d="M15 18l-6-6 6-6" />;
const IcoChevR    = (p: any) => <Ico {...p} d="M9 18l6-6-6-6" />;
const IcoFilter   = (p: any) => <Ico {...p} d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />;
const IcoRefresh  = (p: any) => <Ico {...p} d={['M23 4v6h-6','M1 20v-6h6','M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15']} />;
const IcoClock    = (p: any) => <Ico {...p} d={['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z','M12 6v6l4 2']} />;
const IcoSearch   = (p: any) => <Ico {...p} d={['M11 19a8 8 0 100-16 8 8 0 000 16z','M21 21l-4.35-4.35']} />;
const IcoX        = (p: any) => <Ico {...p} d={['M18 6L6 18','M6 6l12 12']} />;
const IcoBarChart = (p: any) => <Ico {...p} d={['M18 20V10','M12 20V4','M6 20v-6']} />;
const IcoList     = (p: any) => <Ico {...p} d={['M8 6h13','M8 12h13','M8 18h13','M3 6h.01','M3 12h.01','M3 18h.01']} />;
const IcoCalendar = (p: any) => <Ico {...p} d={['M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z','M16 2v4','M8 2v4','M3 10h18']} />;
const IcoLogin    = (p: any) => <Ico {...p} d={['M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4','M10 17l5-5-5-5','M15 12H3']} />;
const IcoLogout   = (p: any) => <Ico {...p} d={['M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4','M16 17l5-5-5-5','M21 12H9']} />;
const IcoGps      = (p: any) => <Ico {...p} d={['M12 2a7 7 0 017 7c0 5.25-7 13-7 13S5 14.25 5 9a7 7 0 017-7z','M12 11a2 2 0 100-4 2 2 0 000 4z']} />;
const IcoWifi     = (p: any) => <Ico {...p} d={['M5 12.55a11 11 0 0114.08 0','M1.42 9a16 16 0 0121.16 0','M8.53 16.11a6 6 0 016.95 0','M12 20h.01']} />;
const IcoDownload = (p: any) => <Ico {...p} d={['M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4','M7 10l5 5 5-5','M12 15V3']} />;
const IcoTrash    = (p: any) => <Ico {...p} d={['M3 6h18','M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6','M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2','M10 11v6','M14 11v6']} />;
const IcoMinus    = (p: any) => <Ico {...p} d="M5 12h14" />;

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&display=swap');

  .at-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }

  @keyframes at-fadeIn  { from{opacity:0}               to{opacity:1} }
  @keyframes at-slideUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  @keyframes at-shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
  @keyframes at-spin    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes at-pulse   { 0%,100%{opacity:1} 50%{opacity:.5} }

  .at-tab-bar {
    display:flex; gap:4px;
    background:rgba(30,58,138,.06);
    border-radius:12px; padding:4px;
  }
  .at-tab {
    display:inline-flex; align-items:center; gap:7px;
    padding:8px 16px; border-radius:9px; border:none;
    font-size:13px; font-weight:600; cursor:pointer;
    font-family:'DM Sans',sans-serif; transition:all .15s;
    background:transparent; color:#6b7280;
  }
  .at-tab:hover { color:#1e3a8a; background:rgba(30,58,138,.06); }
  .at-tab--active { background:white; color:#1e3a8a; box-shadow:0 1px 6px rgba(30,58,138,.14); }

  .at-table { width:100%; border-collapse:collapse; }
  .at-table thead th {
    padding:13px 16px; background:#f9fafb; color:#6b7280;
    font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.7px;
    border-bottom:1px solid #e5e7eb; white-space:nowrap; text-align:left;
  }
  .at-table tbody tr {
    border-bottom:1px solid #f3f4f6; transition:background .13s;
    animation:at-slideUp .25s ease both;
  }
  .at-table tbody tr:hover { background:rgba(59,130,246,.03); }
  .at-table tbody tr:last-child { border-bottom:none; }
  .at-table tbody td { padding:13px 16px; vertical-align:middle; }
  .at-table tbody tr.at-row--unmarked { background:rgba(107,114,128,.025); }
  .at-table tbody tr.at-row--unmarked:hover { background:rgba(107,114,128,.06); }

  .at-badge {
    display:inline-flex; align-items:center; gap:5px;
    padding:3px 10px; border-radius:20px; font-size:11px; font-weight:700;
  }
  .at-badge--present  { background:rgba(34,197,94,.1);  color:#15803d; border:1px solid rgba(34,197,94,.2);  }
  .at-badge--late     { background:rgba(245,158,11,.1); color:#b45309; border:1px solid rgba(245,158,11,.2); }
  .at-badge--absent   { background:rgba(239,68,68,.1);  color:#dc2626; border:1px solid rgba(239,68,68,.2);  }
  .at-badge--unmarked { background:rgba(107,114,128,.1); color:#4b5563; border:1px solid rgba(107,114,128,.2); }

  .at-scan-pill {
    display:inline-flex; align-items:center; gap:4px;
    padding:2px 8px; border-radius:6px; font-size:11px; font-weight:700;
  }
  .at-scan-pill--checkin  { background:rgba(34,197,94,.1);  color:#15803d; }
  .at-scan-pill--checkout { background:rgba(59,130,246,.1); color:#1d4ed8; }

  .at-role {
    display:inline-block; padding:2px 8px; border-radius:6px;
    font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.5px;
    background:rgba(30,58,138,.08); color:#1e3a8a; border:1px solid rgba(30,58,138,.15);
  }

  .at-select {
    padding:8px 12px; border-radius:10px; border:1px solid #e5e7eb;
    background:white; font-size:13px; font-weight:500; color:#374151;
    font-family:'DM Sans',sans-serif; cursor:pointer; outline:none;
    transition:border-color .15s, box-shadow .15s;
  }
  .at-select:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.12); }

  .at-input {
    padding:8px 12px; border-radius:10px; border:1px solid #e5e7eb;
    font-size:13px; font-weight:500; color:#374151;
    font-family:'DM Sans',sans-serif; outline:none; background:white;
    transition:border-color .15s, box-shadow .15s;
  }
  .at-input:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.12); }

  .at-btn {
    display:inline-flex; align-items:center; gap:7px;
    padding:9px 18px; border-radius:11px; border:none;
    font-size:13px; font-weight:600; cursor:pointer;
    font-family:'DM Sans',sans-serif; transition:all .15s;
  }
  .at-btn--primary {
    background:linear-gradient(135deg,#1e3a8a,#3b82f6); color:white;
    box-shadow:0 3px 10px rgba(30,58,138,.28);
  }
  .at-btn--primary:hover { box-shadow:0 5px 16px rgba(30,58,138,.38); transform:translateY(-1px); }
  .at-btn--ghost { background:rgba(30,58,138,.07); color:#1e3a8a; border:1px solid rgba(30,58,138,.15); }
  .at-btn--ghost:hover { background:rgba(30,58,138,.13); }
  .at-btn--warning { background:rgba(245,158,11,.1); color:#b45309; border:1px solid rgba(245,158,11,.25); }
  .at-btn--warning:hover { background:rgba(245,158,11,.18); }
  .at-btn--danger { background:rgba(239,68,68,.08); color:#dc2626; border:1px solid rgba(239,68,68,.2); }
  .at-btn--danger:hover { background:rgba(239,68,68,.15); }
  .at-btn--pdf {
    background:linear-gradient(135deg,#dc2626,#ef4444); color:white;
    box-shadow:0 3px 10px rgba(220,38,38,.28);
  }
  .at-btn--pdf:hover { box-shadow:0 5px 16px rgba(220,38,38,.38); transform:translateY(-1px); }
  .at-btn:disabled { opacity:.5; cursor:not-allowed; transform:none!important; }

  .at-shimmer {
    background:linear-gradient(90deg,#f0f4f8 25%,#e2e8f0 50%,#f0f4f8 75%);
    background-size:600px 100%; animation:at-shimmer 1.3s infinite linear; border-radius:6px;
  }

  .at-stat-card {
    background:white; border-radius:16px; padding:18px 20px;
    border:1px solid #e5e7eb; display:flex; align-items:center; gap:14px;
    box-shadow:0 2px 8px rgba(0,0,0,.05); transition:transform .15s, box-shadow .15s;
    animation:at-slideUp .3s ease both;
  }
  .at-stat-card:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,.09); }

  .at-progress-bg  { height:5px; border-radius:3px; background:#f3f4f6; overflow:hidden; margin-top:6px; }
  .at-progress-fill { height:100%; border-radius:3px; transition:width .5s ease; }

  .at-scroll::-webkit-scrollbar { width:4px; height:4px; }
  .at-scroll::-webkit-scrollbar-track { background:transparent; }
  .at-scroll::-webkit-scrollbar-thumb { background:rgba(30,58,138,.15); border-radius:4px; }

  .at-pg-btn {
    width:34px; height:34px; border-radius:9px; border:1px solid #e5e7eb;
    background:white; cursor:pointer; display:flex; align-items:center; justify-content:center;
    font-size:13px; font-weight:600; color:#374151; transition:all .13s;
  }
  .at-pg-btn:hover:not(:disabled) { background:rgba(30,58,138,.07); border-color:rgba(30,58,138,.2); }
  .at-pg-btn--active { background:linear-gradient(135deg,#1e3a8a,#3b82f6)!important; color:white!important; border-color:transparent!important; }
  .at-pg-btn:disabled { opacity:.4; cursor:not-allowed; }

  .at-live-dot {
    width:8px; height:8px; border-radius:50%; background:#22c55e;
    animation:at-pulse 1.5s ease-in-out infinite;
  }

  .at-mode-pill { display:inline-flex; align-items:center; background:rgba(30,58,138,.06); border-radius:9px; padding:3px; }
  .at-mode-btn {
    padding:5px 11px; border-radius:7px; border:none; cursor:pointer;
    font-size:11px; font-weight:700; font-family:'DM Sans',sans-serif;
    text-transform:uppercase; letter-spacing:.4px; transition:all .13s;
    background:transparent; color:#9ca3af;
  }
  .at-mode-btn--active { background:white; color:#1e3a8a; box-shadow:0 1px 4px rgba(30,58,138,.12); }

  .at-stats-row {
    padding:14px 20px; border-bottom:1px solid #f3f4f6;
    display:flex; align-items:center; gap:14px;
    animation:at-slideUp .25s ease both; transition:background .13s;
  }
  .at-stats-row:hover { background:rgba(59,130,246,.02); }
  .at-stats-row:last-child { border-bottom:none; }

  .at-month-row {
    display:flex; align-items:center; gap:8px; padding:8px 12px;
    border-radius:10px; background:rgba(30,58,138,.04); margin-top:6px;
    font-size:12px; font-weight:600;
  }

  .at-config-banner {
    display:flex; align-items:center; gap:10px; padding:10px 16px;
    border-radius:12px; font-size:12px; font-weight:600;
    border:1px solid; animation:at-fadeIn .3s ease;
  }
  .at-config-banner--loading { background:rgba(30,58,138,.04); border-color:rgba(30,58,138,.12); color:#6b7280; }
  .at-config-banner--ok      { background:rgba(34,197,94,.06); border-color:rgba(34,197,94,.2);  color:#15803d; }
  .at-config-banner--warn    { background:rgba(245,158,11,.08); border-color:rgba(245,158,11,.25); color:#b45309; }

  .at-scan-filter {
    display:inline-flex; align-items:center;
    background:rgba(30,58,138,.06); border-radius:9px; padding:3px;
  }
  .at-scan-filter-btn {
    padding:5px 12px; border-radius:7px; border:none; cursor:pointer;
    font-size:11px; font-weight:700; font-family:'DM Sans',sans-serif;
    text-transform:uppercase; letter-spacing:.4px; transition:all .13s;
    background:transparent; color:#9ca3af;
  }
  .at-scan-filter-btn--active { background:white; color:#1e3a8a; box-shadow:0 1px 4px rgba(30,58,138,.12); }

  .at-pdf-dropdown { position:relative; display:inline-block; }
  .at-pdf-menu {
    position:absolute; right:0; top:calc(100% + 6px); z-index:100;
    background:white; border:1px solid #e5e7eb; border-radius:14px;
    box-shadow:0 8px 32px rgba(0,0,0,.12); min-width:220px;
    padding:6px; animation:at-slideUp .15s ease;
  }
  .at-pdf-menu-item {
    display:flex; align-items:center; gap:10px; padding:10px 14px; border-radius:10px;
    border:none; background:transparent; cursor:pointer; width:100%;
    font-family:'DM Sans',sans-serif; font-size:13px; font-weight:600;
    color:#374151; transition:background .13s; text-align:left;
  }
  .at-pdf-menu-item:hover { background:rgba(30,58,138,.06); color:#1e3a8a; }
  .at-pdf-menu-divider { height:1px; background:#f3f4f6; margin:4px 0; }
  .at-pdf-menu-label {
    padding:6px 14px 4px; font-size:10px; font-weight:700; color:#9ca3af;
    text-transform:uppercase; letter-spacing:.5px;
  }

  /* Delete confirm modal */
  .at-modal-overlay {
    position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:999;
    display:flex; align-items:center; justify-content:center;
    animation:at-fadeIn .15s ease;
  }
  .at-modal {
    background:white; border-radius:20px; padding:28px;
    box-shadow:0 20px 60px rgba(0,0,0,.2); max-width:400px; width:90%;
    animation:at-slideUp .2s ease;
  }
`;

const fmtDate = (d: string) =>
  new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

const fmtTime = (t: string) => {
  try { return new Date(`1970-01-01T${t}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }); }
  catch { return t; }
};

const fmtMonth = (m: string) =>
  new Date(m + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

const TODAY      = new Date().toISOString().substring(0, 10);
const THIS_MONTH = new Date().toISOString().substring(0, 7);

const EMPTY_BUCKET: ScanBucket = { present: 0, absent: 0, late: 0, total: 0 };

type PdfReportType = 'checkin' | 'checkout' | 'all';

async function downloadAttendancePDF(records: AttendanceRecord[], reportType: PdfReportType, periodLabel: string, filterRole: string) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const data = reportType === 'all' ? records : records.filter(r => r.scan_type === reportType);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFillColor(30, 58, 138); doc.rect(0, 0, pageW, 26, 'F');
  doc.setFillColor(59, 130, 246); doc.rect(pageW - 60, 0, 60, 26, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(255, 255, 255);
  doc.text('Staff Attendance Report', 14, 10);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  const scanLabel = reportType === 'checkin' ? 'Check-in only' : reportType === 'checkout' ? 'Check-out only' : 'All scans';
  doc.text(`${scanLabel}  ·  ${periodLabel}${filterRole ? '  ·  Role: ' + filterRole : ''}`, 14, 17);
  doc.setFontSize(7);
  doc.text(`Generated: ${new Date().toLocaleString()}`, pageW - 14, 10, { align: 'right' });
  doc.text(`Total: ${data.length} records`, pageW - 14, 17, { align: 'right' });

  const rows = data.map((r, i) => ({
    idx: i + 1,
    name: r.user_name,
    role: r.user_role.charAt(0).toUpperCase() + r.user_role.slice(1),
    scanType: r.scan_type ? (r.scan_type === 'checkin' ? 'Check-in' : 'Check-out') : '—',
    time: r.scan_time ? fmtTime(r.scan_time) : '—',
    status: r.status === 'unmarked' ? 'Unmarked' : r.status.charAt(0).toUpperCase() + r.status.slice(1),
    stage2: r.stage2_used ? 'Yes' : '—',
  }));

  autoTable(doc, {
    startY: 30,
    columns: [
      { header: '#', dataKey: 'idx' }, { header: 'Staff Name', dataKey: 'name' },
      { header: 'Role', dataKey: 'role' }, { header: 'Scan Type', dataKey: 'scanType' },
      { header: 'Scan Time', dataKey: 'time' }, { header: 'Status', dataKey: 'status' },
      { header: 'Stage 2', dataKey: 'stage2' },
    ],
    body: rows, theme: 'grid', tableWidth: 'auto', margin: { left: 14, right: 14 },
    headStyles: { fillColor: [30, 58, 138], textColor: [255,255,255], fontStyle: 'bold', fontSize: 7.5, cellPadding: { top: 4, bottom: 4, left: 3, right: 3 }, valign: 'middle' },
    bodyStyles: { fontSize: 7.5, cellPadding: { top: 3.5, bottom: 3.5, left: 3, right: 3 }, textColor: [31, 41, 55], valign: 'middle', minCellHeight: 10 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    columnStyles: {
      idx: { cellWidth: 12, halign: 'center', overflow: 'hidden', textColor: [255,255,255] },
      name: { cellWidth: 56, overflow: 'linebreak' }, role: { cellWidth: 26, overflow: 'linebreak' },
      scanType: { cellWidth: 26, halign: 'center', overflow: 'linebreak' },
      time: { cellWidth: 24, halign: 'center', overflow: 'hidden' },
      status: { cellWidth: 24, halign: 'center', overflow: 'hidden' },
      stage2: { cellWidth: 14, halign: 'center', overflow: 'hidden' },
    },
    didDrawCell: (h: any) => {
      if (h.section === 'body' && h.column.dataKey === 'idx') {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
        doc.text(String(h.row.index + 1), h.cell.x + h.cell.width / 2, h.cell.y + h.cell.height / 2 + 2.5, { align: 'center' });
      }
      if (h.section !== 'body') return;
      if (h.column.dataKey === 'status') {
        if (h.cell.raw === 'Present') h.cell.styles.textColor = [21, 128, 61];
        else if (h.cell.raw === 'Late') h.cell.styles.textColor = [180, 83, 9];
        else if (h.cell.raw === 'Absent') h.cell.styles.textColor = [220, 38, 38];
        else if (h.cell.raw === 'Unmarked') h.cell.styles.textColor = [75, 85, 99];
      }
      if (h.column.dataKey === 'scanType') h.cell.styles.textColor = h.cell.raw === 'Check-in' ? [21, 128, 61] : [29, 78, 216];
    },
    foot: [[{ content: `Total: ${data.length} records`, colSpan: 7, styles: { halign: 'right', fontSize: 7.5, fontStyle: 'bold', fillColor: [243, 244, 246], textColor: [30, 58, 138] } }]],
    didDrawPage: (h: any) => {
      const pCount = (doc as any).internal.getNumberOfPages();
      doc.setFontSize(8); doc.setTextColor(156, 163, 175);
      doc.text(`Page ${h.pageNumber} of ${pCount}`, pageW / 2, doc.internal.pageSize.getHeight() - 6, { align: 'center' });
    },
  });

  const safeLabel = periodLabel.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  doc.save(`attendance_${reportType === 'all' ? 'all' : reportType}_${safeLabel}.pdf`);
}

async function downloadStatsPDF(statsData: StatsUserEntry[], periodLabel: string, filterRole: string) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFillColor(30, 58, 138); doc.rect(0, 0, pageW, 26, 'F');
  doc.setFillColor(59, 130, 246); doc.rect(pageW - 60, 0, 60, 26, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(255, 255, 255);
  doc.text('Staff Attendance Stats Report', 14, 10);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text(`${periodLabel}${filterRole ? '  ·  Role: ' + filterRole : ''}`, 14, 17);
  doc.setFontSize(7);
  doc.text(`Generated: ${new Date().toLocaleString()}`, pageW - 14, 10, { align: 'right' });
  doc.text(`Total staff: ${statsData.length}`, pageW - 14, 17, { align: 'right' });

  const rows = statsData.map((u, i) => {
    const ci = u.totals?.checkin ?? u.checkin ?? EMPTY_BUCKET;
    const co = u.totals?.checkout ?? u.checkout ?? EMPTY_BUCKET;
    const totalPresent = ci.present + co.present;
    const totalAll = ci.total + co.total;
    return { idx: String(i + 1), name: u.user_name, role: u.role.charAt(0).toUpperCase() + u.role.slice(1), ciP: ci.present, ciA: ci.absent, ciL: ci.late, coP: co.present, coA: co.absent, coL: co.late, rate: totalAll > 0 ? `${Math.round((totalPresent / totalAll) * 100)}%` : '—' };
  });

  autoTable(doc, {
    startY: 30, margin: { left: 14, right: 14 },
    columns: [{ header: '#', dataKey: 'idx' }, { header: 'Name', dataKey: 'name' }, { header: 'Role', dataKey: 'role' }, { header: 'In·P', dataKey: 'ciP' }, { header: 'In·A', dataKey: 'ciA' }, { header: 'In·L', dataKey: 'ciL' }, { header: 'Out·P', dataKey: 'coP' }, { header: 'Out·A', dataKey: 'coA' }, { header: 'Out·L', dataKey: 'coL' }, { header: 'Rate', dataKey: 'rate' }],
    body: rows, theme: 'grid',
    headStyles: { fillColor: [30, 58, 138], textColor: [255,255,255], fontStyle: 'bold', fontSize: 7.5, cellPadding: { top: 4, bottom: 4, left: 3, right: 3 }, valign: 'middle' },
    bodyStyles: { fontSize: 7.5, textColor: [31, 41, 55], cellPadding: { top: 3.5, bottom: 3.5, left: 3, right: 3 }, valign: 'middle', minCellHeight: 9 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    columnStyles: { idx: { halign: 'center', cellWidth: 8 }, name: { cellWidth: 46 }, role: { cellWidth: 22 }, ciP: { halign: 'center', cellWidth: 14 }, ciA: { halign: 'center', cellWidth: 14 }, ciL: { halign: 'center', cellWidth: 14 }, coP: { halign: 'center', cellWidth: 14 }, coA: { halign: 'center', cellWidth: 14 }, coL: { halign: 'center', cellWidth: 14 }, rate: { halign: 'center', cellWidth: 22 } },
    didDrawCell: (h: any) => {
      if (h.section !== 'body') return;
      if ((h.column.dataKey === 'ciP' || h.column.dataKey === 'coP') && h.cell.raw > 0) h.cell.styles.textColor = [21, 128, 61];
      if ((h.column.dataKey === 'ciA' || h.column.dataKey === 'coA') && h.cell.raw > 0) h.cell.styles.textColor = [220, 38, 38];
      if ((h.column.dataKey === 'ciL' || h.column.dataKey === 'coL') && h.cell.raw > 0) h.cell.styles.textColor = [180, 83, 9];
      if (h.column.dataKey === 'rate') h.cell.styles.textColor = [30, 58, 138];
    },
    didDrawPage: (h: any) => {
      const pCount = (doc as any).internal.getNumberOfPages();
      doc.setFontSize(7.5); doc.setTextColor(156, 163, 175);
      doc.text(`Page ${h.pageNumber} of ${pCount}`, pageW / 2, doc.internal.pageSize.getHeight() - 6, { align: 'center' });
    },
  });

  doc.save(`attendance_stats_${periodLabel.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
}

const PdfDownloadButton: React.FC<{
  records: AttendanceRecord[]; periodLabel: string; filterRole: string;
  disabled?: boolean; statsData?: StatsUserEntry[]; statsPeriodLabel?: string;
}> = ({ records, periodLabel, filterRole, disabled, statsData, statsPeriodLabel }) => {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const isStatsMode = !!statsData;

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleDownload = async (type: PdfReportType | 'stats') => {
    setOpen(false); setGenerating(type);
    try {
      if (type === 'stats' && statsData) await downloadStatsPDF(statsData, statsPeriodLabel ?? periodLabel, filterRole);
      else await downloadAttendancePDF(records, type as PdfReportType, periodLabel, filterRole);
    } finally { setGenerating(null); }
  };

  const isLoading = generating !== null;
  const hasData = isStatsMode ? (statsData?.length ?? 0) > 0 : records.length > 0;
  const recordOptions: { type: PdfReportType; label: string; sub: string; color: string }[] = [
    { type: 'checkin', label: 'Check-in Report', sub: 'Only check-in scans', color: '#15803d' },
    { type: 'checkout', label: 'Check-out Report', sub: 'Only check-out scans', color: '#1d4ed8' },
    { type: 'all', label: 'Full Report', sub: 'All scans combined', color: '#1e3a8a' },
  ];

  return (
    <div className="at-pdf-dropdown" ref={ref}>
      <button className="at-btn at-btn--pdf" onClick={() => setOpen(o => !o)} disabled={disabled || isLoading || !hasData}>
        {isLoading ? <IcoRefresh size={15} color="white" style={{ animation: 'at-spin 1s linear infinite' }} /> : <IcoDownload size={15} color="white" />}
        {isLoading ? 'Generating…' : 'Download PDF'}
        {!isLoading && <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: -2, transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}><path d="M6 9l6 6 6-6" /></svg>}
      </button>
      {open && !isLoading && (
        <div className="at-pdf-menu">
          {isStatsMode ? (
            <>
              <div className="at-pdf-menu-label">Export Stats as PDF</div>
              <button className="at-pdf-menu-item" onClick={() => handleDownload('stats')}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(30,58,138,.1)', border: '1px solid rgba(30,58,138,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IcoBarChart size={14} color="#1e3a8a" /></div>
                <div><div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Stats Report</div><div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>Per-staff attendance breakdown</div></div>
                <div style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#1e3a8a', background: 'rgba(30,58,138,.08)', padding: '2px 7px', borderRadius: 6 }}>{statsData?.length} staff</div>
              </button>
            </>
          ) : (
            <>
              <div className="at-pdf-menu-label">Export Records as PDF</div>
              {recordOptions.map(opt => (
                <button key={opt.type} className="at-pdf-menu-item" onClick={() => handleDownload(opt.type)}>
                  <div style={{ width: 32, height: 32, borderRadius: 9, background: `${opt.color}18`, border: `1px solid ${opt.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IcoDownload size={14} color={opt.color} /></div>
                  <div><div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{opt.label}</div><div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{opt.sub}</div></div>
                  <div style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: opt.color, background: `${opt.color}12`, padding: '2px 7px', borderRadius: 6 }}>
                    {opt.type === 'all' ? records.length : records.filter(r => r.scan_type === opt.type).length}
                  </div>
                </button>
              ))}
            </>
          )}
          <div className="at-pdf-menu-divider" />
          <div style={{ padding: '4px 14px 6px', fontSize: 11, color: '#9ca3af' }}>
            {isStatsMode ? `${statsData?.length ?? 0} staff member${(statsData?.length ?? 0) !== 1 ? 's' : ''} in current view` : `${records.length} record${records.length !== 1 ? 's' : ''} in current view`}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
const DeleteModal: React.FC<{
  record: AttendanceRecord | null;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ record, onConfirm, onCancel, loading }) => {
  if (!record) return null;
  return (
    <div className="at-modal-overlay" onClick={onCancel}>
      <div className="at-modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
          <div style={{ width: 46, height: 46, borderRadius: 13, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IcoTrash size={20} color="#dc2626" />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Delete Attendance Entry</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>This cannot be undone</div>
          </div>
        </div>
        <div style={{ background: '#f9fafb', borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: 13 }}>
          <div style={{ fontWeight: 700, color: '#111827', marginBottom: 4 }}>{record.user_name}</div>
          <div style={{ color: '#6b7280' }}>
            {record.scan_type === 'checkin' ? 'Check-in' : 'Check-out'} on {fmtDate(record.attendance_date)} at {record.scan_time ? fmtTime(record.scan_time) : '—'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="at-btn at-btn--ghost" onClick={onCancel} disabled={loading}>Cancel</button>
          <button className="at-btn" onClick={onConfirm} disabled={loading}
            style={{ background: 'linear-gradient(135deg,#dc2626,#ef4444)', color: 'white', boxShadow: '0 3px 10px rgba(220,38,38,.28)' }}>
            {loading ? <IcoRefresh size={14} color="white" style={{ animation: 'at-spin 1s linear infinite' }} /> : <IcoTrash size={14} color="white" />}
            {loading ? 'Deleting…' : 'Delete Entry'}
          </button>
        </div>
      </div>
    </div>
  );
};

type DateMode   = 'day' | 'month' | 'range';
type ScanFilter = 'all' | 'checkin' | 'checkout';

const StatCard: React.FC<{ label: string; value: string | number; sub?: string; icon: React.ReactNode; gradient: string; delay?: number }> = ({ label, value, sub, icon, gradient, delay = 0 }) => (
  <div className="at-stat-card" style={{ animationDelay: `${delay}s` }}>
    <div style={{ width: 46, height: 46, borderRadius: 13, flexShrink: 0, background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,.18)' }}>{icon}</div>
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{sub}</div>}
    </div>
  </div>
);

const SkeletonRow: React.FC<{ i: number; cols?: number }> = ({ i, cols = 10 }) => (
  <tr style={{ animationDelay: `${i * 0.05}s` }}>
    {[28, 160, 70, 80, 80, 80, 80, 80, 60, 40].slice(0, cols).map((w, j) => (
      <td key={j}><div className="at-shimmer" style={{ height: 14, width: w, maxWidth: '100%' }} /></td>
    ))}
  </tr>
);

const MiniBar: React.FC<{ value: number; total: number; color: string }> = ({ value, total, color }) => (
  <div className="at-progress-bg" style={{ width: 80 }}>
    <div className="at-progress-fill" style={{ width: `${total ? (value / total) * 100 : 0}%`, background: color }} />
  </div>
);

const StaffAttendance: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [activeTab, setActiveTab] = useState<'records' | 'stats'>('records');
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({ current_page: 1, total_pages: 1, total_items: 0, per_page: 20, has_next: false, has_previous: false });

  const [statsData, setStatsData] = useState<StatsUserEntry[]>([]);
  const [statsSummary, setStatsSummary] = useState<StatsSummary>({ checkin: EMPTY_BUCKET, checkout: EMPTY_BUCKET });
  const [statsLoading, setStatsLoading] = useState(false);
  const [expandedUser, setExpandedUser] = useState<number | null>(null);

  const [dateMode, setDateMode]     = useState<DateMode>('day');
  const [filterDate, setFilterDate] = useState(TODAY);
  const [filterMonth, setFilterMonth] = useState(THIS_MONTH);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo]     = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterScan, setFilterScan] = useState<ScanFilter>('all');
  const [filterSearch, setFilterSearch] = useState('');
  const [page, setPage]             = useState(1);
  const [pageSize, setPageSize]     = useState(20);

  // Show unmarked (all users) toggle — only relevant for day mode
  const [showAllUsers, setShowAllUsers] = useState(true);

  const [barcodeOpen, setBarcodeOpen] = useState(false);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<AttendanceRecord | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { options: configOptions, loading: configLoading } = useOptions<any>([
    'attendance_location', 'checkin_window', 'checkout_window', 'gps_only_attendance',
  ]);

  const locationCfg    = configOptions['attendance_location']?.data  as AttendanceLocation | null;
  const checkinWindow  = configOptions['checkin_window']?.data       as WindowOption       | null;
  const checkoutWindow = configOptions['checkout_window']?.data      as WindowOption       | null;
  const gpsOnly        = configOptions['gps_only_attendance']?.data  as GpsOnlyOption      | null;

  const locationReady  = !!locationCfg?.enabled;
  const checkinActive  = !!checkinWindow?.enabled;
  const checkoutActive = !!checkoutWindow?.enabled;
  const gpsOnlyMode    = !!gpsOnly?.enabled;

  const buildParams = useCallback(() => {
    const params: Record<string, string | number> = {};
    if (filterRole) params.user_role = filterRole;
    if (filterScan !== 'all') params.scan_type = filterScan;
    if (dateMode === 'day'   && filterDate)  params.date       = filterDate;
    if (dateMode === 'month' && filterMonth) params.month      = filterMonth;
    if (dateMode === 'range') {
      if (filterFrom) params.month_from = filterFrom;
      if (filterTo)   params.month_to   = filterTo;
    }
    return params;
  }, [dateMode, filterDate, filterMonth, filterFrom, filterTo, filterRole, filterScan]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { ...buildParams(), page, limit: pageSize };
      // Only send show_all_users=1 in day mode (backend generates unmarked placeholders)
      if (dateMode === 'day' && showAllUsers) params.show_all_users = 1;
      const res = await apiClient.get('/staff-attendance', { params });
      if (res.data.status === 'success') {
        setRecords(res.data.data.records ?? []);
        setPagination(res.data.data.pagination);
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || 'Failed to load records', life: 4000 });
    } finally { setLoading(false); }
  }, [buildParams, page, pageSize, dateMode, showAllUsers]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await apiClient.get('/staff-attendance/stats', { params: buildParams() });
      if (res.data.status === 'success') {
        setStatsData(res.data.data.stats ?? []);
        setStatsSummary(res.data.data.summary ?? { checkin: EMPTY_BUCKET, checkout: EMPTY_BUCKET });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || 'Failed to load stats', life: 4000 });
    } finally { setStatsLoading(false); }
  }, [buildParams]);

  useEffect(() => {
    if (activeTab === 'records') fetchRecords();
    else fetchStats();
  }, [activeTab, fetchRecords, fetchStats]);

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    try {
      const res = await apiClient.delete(`/staff-attendance/${deleteTarget.id}`);
      if (res.data.status === 'success') {
        toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Attendance entry removed.', life: 3000 });
        setDeleteTarget(null);
        fetchRecords();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message || 'Delete failed', life: 4000 });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || 'Delete failed', life: 4000 });
    } finally { setDeleteLoading(false); }
  };

  const setMode = (m: DateMode) => { setDateMode(m); setPage(1); };
  const clearFilters = () => {
    setDateMode('day'); setFilterDate(TODAY); setFilterMonth(THIS_MONTH);
    setFilterFrom(''); setFilterTo(''); setFilterRole(''); setFilterScan('all');
    setFilterSearch(''); setPage(1); setShowAllUsers(true);
  };

  const displayed = filterSearch.trim()
    ? records.filter(r =>
        r.user_name?.toLowerCase().includes(filterSearch.toLowerCase()) ||
        r.user_email?.toLowerCase().includes(filterSearch.toLowerCase()) ||
        r.user_role?.toLowerCase().includes(filterSearch.toLowerCase())
      )
    : records;

  const checkinCount   = displayed.filter(r => r.scan_type === 'checkin').length;
  const checkoutCount  = displayed.filter(r => r.scan_type === 'checkout').length;
  const unmarkedCount  = displayed.filter(r => r.is_unmarked_placeholder).length;
  const stage2Count    = displayed.filter(r => r.stage2_used).length;

  const pageNums = () => {
    const total = pagination.total_pages, cur = pagination.current_page;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (cur > 3) pages.push('...');
    for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) pages.push(i);
    if (cur < total - 2) pages.push('...');
    pages.push(total);
    return pages;
  };

  const periodLabel = () => {
    if (dateMode === 'day')   return filterDate ? fmtDate(filterDate) : 'Today';
    if (dateMode === 'month') return filterMonth ? fmtMonth(filterMonth) : 'This Month';
    if (filterFrom && filterTo) return `${fmtMonth(filterFrom)} to ${fmtMonth(filterTo)}`;
    if (filterFrom) return `From ${fmtMonth(filterFrom)}`;
    if (filterTo)   return `Until ${fmtMonth(filterTo)}`;
    return 'All time';
  };

  const renderConfigBanner = () => {
    if (configLoading) return (
      <div className="at-config-banner at-config-banner--loading" style={{ marginBottom: 14 }}>
        <IcoRefresh size={13} color="#6b7280" style={{ animation: 'at-spin 1s linear infinite' }} />
        Loading attendance configuration…
      </div>
    );
    const issues: string[] = [];
    if (!locationReady)  issues.push('School location not configured');
    if (!checkinActive)  issues.push('Check-in window disabled');
    if (!checkoutActive) issues.push('Check-out window disabled');
    if (issues.length > 0) return (
      <div className="at-config-banner at-config-banner--warn" style={{ marginBottom: 14 }}>
        <IcoAlert size={13} color="#b45309" />{issues.join(' · ')}
      </div>
    );
    return (
      <div className="at-config-banner at-config-banner--ok" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
        <IcoCheck size={13} color="#15803d" />
        <span>Location active · {locationCfg!.radius}m radius · Check-in {checkinWindow!.start}–{checkinWindow!.end} · Check-out {checkoutWindow!.start}–{checkoutWindow!.end}{gpsOnlyMode && <> · <strong>GPS-only mode</strong></>}</span>
      </div>
    );
  };

  const renderDateFilters = () => (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <label style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>Period</label>
        <div className="at-mode-pill">
          {(['day', 'month', 'range'] as DateMode[]).map(m => (
            <button key={m} className={`at-mode-btn${dateMode === m ? ' at-mode-btn--active' : ''}`} onClick={() => setMode(m)}>
              {m === 'day' ? 'Day' : m === 'month' ? 'Month' : 'Range'}
            </button>
          ))}
        </div>
      </div>
      {dateMode === 'day' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>Date</label>
          <input type="date" className="at-input" value={filterDate} onChange={e => { setFilterDate(e.target.value); setPage(1); }} style={{ width: 155 }} />
        </div>
      )}
      {dateMode === 'month' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>Month</label>
          <input type="month" className="at-input" value={filterMonth} onChange={e => { setFilterMonth(e.target.value); setPage(1); }} style={{ width: 160 }} />
        </div>
      )}
      {dateMode === 'range' && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>From</label>
            <input type="month" className="at-input" value={filterFrom} onChange={e => { setFilterFrom(e.target.value); setPage(1); }} style={{ width: 155 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>To</label>
            <input type="month" className="at-input" value={filterTo} onChange={e => { setFilterTo(e.target.value); setPage(1); }} style={{ width: 155 }} />
          </div>
        </>
      )}
    </div>
  );

  const renderStats = () => {
    const isRange = dateMode === 'range';
    const totalPresent = statsSummary.checkin.present + statsSummary.checkout.present;
    const totalAbsent  = statsSummary.checkin.absent  + statsSummary.checkout.absent;
    const totalLate    = statsSummary.checkin.late    + statsSummary.checkout.late;
    const totalAll     = statsSummary.checkin.total   + statsSummary.checkout.total;

    return (
      <div style={{ background: 'white', borderRadius: 20, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 2px 16px rgba(30,58,138,.07)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', background: 'linear-gradient(135deg,#fff,#fafbfc)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(30,58,138,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IcoBarChart size={14} color="#1e3a8a" /></div>
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>{statsLoading ? 'Loading…' : `${statsData.length} staff · ${periodLabel()}`}</span>
          </div>
          {!statsLoading && totalAll > 0 && (
            <div style={{ display: 'flex', gap: 18 }}>
              <div style={{ display: 'flex', gap: 10, paddingRight: 18, borderRight: '1px solid #e5e7eb' }}>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: 12, fontWeight: 800, color: '#15803d' }}>{statsSummary.checkin.present}</div><div style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.3px' }}>In·P</div></div>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8' }}>{statsSummary.checkout.present}</div><div style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.3px' }}>Out·P</div></div>
              </div>
              {[{ label: 'Present', value: totalPresent, color: '#15803d' }, { label: 'Absent', value: totalAbsent, color: '#dc2626' }, { label: 'Late', value: totalLate, color: '#b45309' }, { label: 'Total', value: totalAll, color: '#1e3a8a' }].map(s => (
                <div key={s.label} style={{ textAlign: 'center' }}><div style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.value}</div><div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.4px' }}>{s.label}</div></div>
              ))}
            </div>
          )}
        </div>
        {statsLoading ? (
          <div style={{ padding: 24 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                <div className="at-shimmer" style={{ width: 36, height: 36, borderRadius: 10 }} />
                <div style={{ flex: 1 }}><div className="at-shimmer" style={{ height: 12, width: 140, marginBottom: 6 }} /><div className="at-shimmer" style={{ height: 10, width: 90 }} /></div>
                <div className="at-shimmer" style={{ height: 10, width: 220 }} />
              </div>
            ))}
          </div>
        ) : statsData.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: 12 }}>
            <div style={{ width: 54, height: 54, borderRadius: 14, background: 'rgba(30,58,138,.06)', border: '1.5px dashed rgba(59,130,246,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IcoBarChart size={24} color="#93c5fd" /></div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#374151' }}>No stats available</div>
            <div style={{ fontSize: 13, color: '#9ca3af' }}>Try adjusting your filters or date range.</div>
          </div>
        ) : (
          <div>
            {statsData.map((user, idx) => {
              const isExpanded = expandedUser === user.user_id;
              const hasMonths  = isRange && user.months && user.months.length > 0;
              const ci = isRange ? (user.totals?.checkin ?? EMPTY_BUCKET) : (user.checkin ?? EMPTY_BUCKET);
              const co = isRange ? (user.totals?.checkout ?? EMPTY_BUCKET) : (user.checkout ?? EMPTY_BUCKET);
              const totalPresent = ci.present + co.present;
              const totalAll     = ci.total + co.total;
              return (
                <div key={user.user_id} style={{ borderBottom: idx < statsData.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <div className="at-stats-row" style={{ cursor: hasMonths ? 'pointer' : 'default' }} onClick={() => hasMonths && setExpandedUser(isExpanded ? null : user.user_id)}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: 'white' }}>{user.user_name?.charAt(0)?.toUpperCase() ?? '?'}</div>
                    <div style={{ minWidth: 160 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{user.user_name}</div>
                      <span className="at-role" style={{ marginTop: 3, display: 'inline-block' }}>{user.role}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flex: 1, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: 12, padding: '6px 12px', borderRadius: 10, background: 'rgba(34,197,94,.05)', border: '1px solid rgba(34,197,94,.12)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}><IcoLogin size={11} color="#15803d" /><span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '.4px' }}>Check-in</span></div>
                        {[{ label: 'P', val: ci.present, color: '#15803d' }, { label: 'A', val: ci.absent, color: '#dc2626' }, { label: 'L', val: ci.late, color: '#b45309' }].map(s => (
                          <div key={s.label} style={{ textAlign: 'center', minWidth: 24 }}><div style={{ fontSize: 14, fontWeight: 800, color: s.color }}>{s.val}</div><div style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>{s.label}</div></div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 12, padding: '6px 12px', borderRadius: 10, background: 'rgba(59,130,246,.05)', border: '1px solid rgba(59,130,246,.12)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}><IcoLogout size={11} color="#1d4ed8" /><span style={{ fontSize: 10, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '.4px' }}>Check-out</span></div>
                        {[{ label: 'P', val: co.present, color: '#15803d' }, { label: 'A', val: co.absent, color: '#dc2626' }, { label: 'L', val: co.late, color: '#b45309' }].map(s => (
                          <div key={s.label} style={{ textAlign: 'center', minWidth: 24 }}><div style={{ fontSize: 14, fontWeight: 800, color: s.color }}>{s.val}</div><div style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>{s.label}</div></div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 60 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#1e3a8a' }}>{totalAll > 0 ? `${Math.round((totalPresent / totalAll) * 100)}%` : '—'}</div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.4px' }}>Rate</div>
                        <MiniBar value={totalPresent} total={totalAll} color="rgba(30,58,138,.5)" />
                      </div>
                    </div>
                    {hasMonths && <div style={{ transition: 'transform .2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)' }}><IcoChevR size={14} color="#9ca3af" /></div>}
                  </div>
                  {isExpanded && hasMonths && (
                    <div style={{ padding: '4px 20px 16px 70px', animation: 'at-fadeIn .2s ease' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {user.months!.map(m => (
                          <div key={m.month} className="at-month-row">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginRight: 6 }}><IcoCalendar size={11} color="#6b7280" /><span style={{ color: '#374151', fontWeight: 700, fontSize: 12 }}>{fmtMonth(m.month)}</span></div>
                            <span style={{ color: '#15803d', fontSize: 11 }}>In:{m.checkin.present}</span>
                            <span style={{ color: '#1d4ed8', fontSize: 11 }}>Out:{m.checkout.present}</span>
                            {(m.checkin.absent + m.checkout.absent) > 0 && <span style={{ color: '#dc2626', fontSize: 11 }}>A:{m.checkin.absent + m.checkout.absent}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <style>{CSS}</style>

      <DeleteModal
        record={deleteTarget}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteLoading}
      />

      <div className="at-root" style={{ minHeight: '100vh' }}>

        {/* Page header */}
        <div style={{ background: 'white', borderRadius: 20, padding: '20px 24px', marginBottom: 16, boxShadow: '0 2px 12px rgba(0,0,0,.06)', border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 50, height: 50, background: 'linear-gradient(135deg,#1e3a8a 0%,#3b82f6 100%)', borderRadius: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(30,58,138,.25)', flexShrink: 0 }}>
              <IcoShield size={22} color="white" />
            </div>
            <div>
              <h1 style={{ margin: 0, color: '#1f2937', fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px' }}>Staff Attendance</h1>
              <div style={{ color: '#6b7280', fontSize: 13, marginTop: 3, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 7 }}>
                <div className="at-live-dot" />
                {loading || statsLoading ? 'Loading…' : periodLabel()}
                {gpsOnlyMode && <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '1px 7px', borderRadius: 6, background: 'rgba(30,58,138,.08)', fontSize: 11, fontWeight: 700, color: '#1e3a8a' }}><IcoGps size={10} color="#1e3a8a" /> GPS-only</span>}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div className="at-tab-bar">
              <button className={`at-tab${activeTab === 'records' ? ' at-tab--active' : ''}`} onClick={() => setActiveTab('records')}><IcoList size={13} color={activeTab === 'records' ? '#1e3a8a' : '#9ca3af'} />Records</button>
              <button className={`at-tab${activeTab === 'stats' ? ' at-tab--active' : ''}`} onClick={() => setActiveTab('stats')}><IcoBarChart size={13} color={activeTab === 'stats' ? '#1e3a8a' : '#9ca3af'} />Stats</button>
            </div>
            {gpsOnlyMode ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 11, background: 'rgba(34,197,94,.08)', border: '1px solid rgba(34,197,94,.2)' }}>
                <IcoWifi size={14} color="#15803d" /><span style={{ fontSize: 13, fontWeight: 600, color: '#15803d' }}>GPS-only mode</span>
              </div>
            ) : (
              <button className="at-btn at-btn--primary" onClick={() => setBarcodeOpen(true)}><IcoQr size={15} color="white" />Show QR Code</button>
            )}
            <PdfDownloadButton
              records={activeTab === 'records' ? displayed : []}
              periodLabel={periodLabel()}
              filterRole={filterRole}
              disabled={loading || statsLoading}
              statsData={activeTab === 'stats' ? statsData : undefined}
              statsPeriodLabel={periodLabel()}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 11, background: locationReady ? 'rgba(34,197,94,.06)' : 'rgba(239,68,68,.06)', border: `1px solid ${locationReady ? 'rgba(34,197,94,.2)' : 'rgba(239,68,68,.2)'}` }}>
              <IcoMapPin size={13} color={locationReady ? '#15803d' : '#dc2626'} />
              <span style={{ fontSize: 12, fontWeight: 600, color: locationReady ? '#15803d' : '#dc2626' }}>{configLoading ? '…' : locationCfg ? `${locationCfg.radius}m radius` : 'No location'}</span>
            </div>
          </div>
        </div>

        {renderConfigBanner()}

        {activeTab === 'records' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14, marginBottom: 20 }}>
            <StatCard label="Total Records" value={pagination.total_items} sub={periodLabel()} icon={<IcoUsers size={20} color="white" />} gradient="linear-gradient(135deg,#1e3a8a,#3b82f6)" delay={0} />
            <StatCard label="Check-ins" value={checkinCount} sub={checkinWindow ? `Window: ${checkinWindow.start}–${checkinWindow.end}` : undefined} icon={<IcoLogin size={20} color="white" />} gradient="linear-gradient(135deg,#065f46,#22c55e)" delay={0.05} />
            <StatCard label="Check-outs" value={checkoutCount} sub={checkoutWindow ? `Window: ${checkoutWindow.start}–${checkoutWindow.end}` : undefined} icon={<IcoLogout size={20} color="white" />} gradient="linear-gradient(135deg,#1d4ed8,#60a5fa)" delay={0.1} />
            {dateMode === 'day' && showAllUsers && (
              <StatCard label="Unmarked" value={unmarkedCount} sub="No scan recorded today" icon={<IcoMinus size={20} color="white" />} gradient="linear-gradient(135deg,#374151,#6b7280)" delay={0.15} />
            )}
            <StatCard label="Stage 2 Passes" value={stage2Count} sub="Location override used" icon={<IcoAlert size={20} color="white" />} gradient="linear-gradient(135deg,#92400e,#f59e0b)" delay={0.2} />
          </div>
        )}

        {/* Filters */}
        <div style={{ background: 'white', borderRadius: 16, padding: '16px 20px', marginBottom: 16, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginBottom: 2 }}>
            <IcoFilter size={14} color="#6b7280" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px' }}>Filters</span>
          </div>
          {renderDateFilters()}

          {/* Scan type filter */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>Scan</label>
            <div className="at-scan-filter">
              {(['all', 'checkin', 'checkout'] as ScanFilter[]).map(s => (
                <button key={s} className={`at-scan-filter-btn${filterScan === s ? ' at-scan-filter-btn--active' : ''}`} onClick={() => { setFilterScan(s); setPage(1); }}>
                  {s === 'all' ? 'All' : s === 'checkin' ? 'In' : 'Out'}
                </button>
              ))}
            </div>
          </div>

          {/* Role */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>Role</label>
            <select className="at-select" value={filterRole} onChange={e => { setFilterRole(e.target.value); setPage(1); }}>
              <option value="">All Roles</option>
              <option value="teacher">Teacher</option>
              <option value="principal">Principal</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Super Admin</option>
            </select>
          </div>

          {/* Show unmarked toggle (day mode only) */}
          {activeTab === 'records' && dateMode === 'day' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>Show Unmarked</label>
              <button
                className={`at-btn ${showAllUsers ? 'at-btn--warning' : 'at-btn--ghost'}`}
                style={{ padding: '8px 14px', fontSize: 12 }}
                onClick={() => { setShowAllUsers(v => !v); setPage(1); }}
              >
                <IcoUsers size={13} color={showAllUsers ? '#b45309' : '#1e3a8a'} />
                {showAllUsers ? 'Showing Unmarked' : 'Show Unmarked'}
              </button>
            </div>
          )}

          {/* Search */}
          {activeTab === 'records' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 180 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>Search</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <IcoSearch size={13} color="#9ca3af" style={{ position: 'absolute', left: 10 }} />
                <input className="at-input" placeholder="Name, email, role…" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} style={{ paddingLeft: 32, width: '100%' }} />
                {filterSearch && (
                  <button onClick={() => setFilterSearch('')} style={{ position: 'absolute', right: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}>
                    <IcoX size={12} color="#9ca3af" />
                  </button>
                )}
              </div>
            </div>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <button className="at-btn at-btn--ghost" style={{ padding: '8px 12px' }} onClick={clearFilters} title="Clear"><IcoX size={13} color="#1e3a8a" /></button>
            <button className="at-btn at-btn--ghost" style={{ padding: '8px 12px' }} onClick={() => activeTab === 'records' ? fetchRecords() : fetchStats()} title="Refresh">
              <IcoRefresh size={13} color="#1e3a8a" style={{ animation: (loading || statsLoading) ? 'at-spin 1s linear infinite' : 'none' }} />
            </button>
          </div>
        </div>

        {activeTab === 'stats' && renderStats()}

        {activeTab === 'records' && (
          <div style={{ background: 'white', borderRadius: 20, boxShadow: '0 2px 16px rgba(30,58,138,.07)', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', background: 'linear-gradient(135deg,#fff,#fafbfc)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(30,58,138,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IcoUsers size={14} color="#1e3a8a" /></div>
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                  {loading ? 'Loading…' : `${displayed.length} of ${pagination.total_items} records`}
                  <span style={{ color: '#3b82f6', marginLeft: 6 }}>for {periodLabel()}</span>
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>Per page</span>
                <select className="at-select" value={pageSize} style={{ padding: '5px 8px', fontSize: 12 }} onChange={e => { setPageSize(+e.target.value); setPage(1); }}>
                  {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>

            <div className="at-scroll" style={{ overflowX: 'auto' }}>
              <table className="at-table">
                <thead>
                  <tr>
                    <th>#</th><th>Staff Member</th><th>Role</th><th>Date</th>
                    <th>Scan Type</th><th>Scan Time</th><th>Status</th>
                    <th>Distance</th><th>Stage 2</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} i={i} cols={10} />)
                    : displayed.length === 0
                      ? (
                        <tr><td colSpan={10}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: 12, animation: 'at-fadeIn .3s ease' }}>
                            <div style={{ width: 54, height: 54, borderRadius: 14, background: 'rgba(30,58,138,.06)', border: '1.5px dashed rgba(59,130,246,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IcoShield size={24} color="#93c5fd" /></div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#374151' }}>No records found</div>
                            <div style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>Try adjusting your filters or date range.</div>
                          </div>
                        </td></tr>
                      )
                      : displayed.map((rec, i) => (
                        <tr key={rec.id ?? `unmarked-${rec.user_id}`} style={{ animationDelay: `${i * 0.04}s` }} className={rec.is_unmarked_placeholder ? 'at-row--unmarked' : ''}>
                          <td>
                            <div style={{ width: 28, height: 28, background: '#f9fafb', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#6b7280', border: '1px solid #e5e7eb' }}>
                              {(page - 1) * pageSize + i + 1}
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <ProfilePictureHover
                                src={rec.profile_picture ?? null}
                                name={rec.user_name}
                                cacheBust={rec.created_at ? new Date(rec.created_at).getTime().toString() : '0'}
                                size={38} previewSize={240} borderRadius={10}
                              />
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{rec.user_name}</div>
                                <div style={{ fontSize: 11, color: '#9ca3af' }}>{rec.user_email}</div>
                              </div>
                            </div>
                          </td>
                          <td><span className="at-role">{rec.user_role}</span></td>
                          <td style={{ fontSize: 13, color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>{fmtDate(rec.attendance_date)}</td>
                          <td>
                            {rec.scan_type ? (
                              <span className={`at-scan-pill at-scan-pill--${rec.scan_type}`}>
                                {rec.scan_type === 'checkin' ? <><IcoLogin size={10} color="#15803d" /> Check-in</> : <><IcoLogout size={10} color="#1d4ed8" /> Check-out</>}
                              </span>
                            ) : (
                              <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>
                            )}
                          </td>
                          <td>
                            {rec.scan_time ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <IcoClock size={12} color="#9ca3af" />
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', fontFamily: 'monospace' }}>{fmtTime(rec.scan_time)}</span>
                              </div>
                            ) : <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>}
                          </td>
                          <td>
                            <span className={`at-badge at-badge--${rec.status}`}>
                              {rec.status === 'present'  && <IcoCheck  size={9} color="#15803d" />}
                              {rec.status === 'late'     && <IcoClock  size={9} color="#b45309" />}
                              {rec.status === 'absent'   && <IcoAlert  size={9} color="#dc2626" />}
                              {rec.status === 'unmarked' && <IcoMinus  size={9} color="#4b5563" />}
                              {rec.status === 'unmarked' ? 'Unmarked' : rec.status.charAt(0).toUpperCase() + rec.status.slice(1)}
                            </span>
                          </td>
                          <td>
                            {rec.distance_m != null
                              ? <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700, fontFamily: 'monospace', background: rec.distance_m <= (locationCfg?.radius ?? 9999) ? 'rgba(34,197,94,.08)' : 'rgba(239,68,68,.08)', color: rec.distance_m <= (locationCfg?.radius ?? 9999) ? '#15803d' : '#dc2626' }}>{rec.distance_m}m</span>
                              : <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>}
                          </td>
                          <td>
                            {rec.stage2_used
                              ? <span style={{ padding: '2px 9px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: 'rgba(245,158,11,.1)', color: '#b45309', border: '1px solid rgba(245,158,11,.2)' }}>Yes</span>
                              : <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>}
                          </td>
                          <td>
                            {!rec.is_unmarked_placeholder && rec.id && (
                              <button
                                className="at-btn at-btn--danger"
                                style={{ padding: '5px 10px', fontSize: 11 }}
                                onClick={() => setDeleteTarget(rec)}
                                title="Delete this entry"
                              >
                                <IcoTrash size={12} color="#dc2626" />
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                  }
                </tbody>
              </table>
            </div>

            {pagination.total_pages > 1 && (
              <div style={{ padding: '14px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>Page {pagination.current_page} of {pagination.total_pages} · {pagination.total_items} total</span>
                <div style={{ display: 'flex', gap: 5 }}>
                  <button className="at-pg-btn" disabled={!pagination.has_previous} onClick={() => setPage(p => p - 1)}><IcoChevL size={13} color="#374151" /></button>
                  {pageNums().map((n, i) =>
                    n === '...'
                      ? <span key={i} style={{ width: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>…</span>
                      : <button key={i} className={`at-pg-btn${n === pagination.current_page ? ' at-pg-btn--active' : ''}`} onClick={() => setPage(n as number)}>{n}</button>
                  )}
                  <button className="at-pg-btn" disabled={!pagination.has_next} onClick={() => setPage(p => p + 1)}><IcoChevR size={13} color="#374151" /></button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <AttendanceBarcodeModal open={barcodeOpen} onClose={() => setBarcodeOpen(false)} />
    </>
  );
};

export default StaffAttendance;